import {Box} from '@viptech/react-components'
import {useCallback, useEffect, useRef, useState} from 'react'
import {Coordinate} from 'src/services/api'
import {Dim} from '../../types/generics/ImageDetections'

type Props = {
  points?: Array<Coordinate>
  imageSrc: string
  width: number
  height: number
}

export type CoordinatesType = {x: number; y: number; reference: string}

export const DrawBarrierWithSvg = (props: Props) => {
  const {points, imageSrc, width, height} = props
  const imgRef = useRef<HTMLImageElement | null>(null)
  const [renderedDim, setRenderedDim] = useState<Dim>({width: 0, height: 0})

  const updateSize = useCallback(() => {
    if (!imgRef.current) return
    const {clientWidth, clientHeight} = imgRef.current
    setRenderedDim({width: clientWidth, height: clientHeight})
  }, [])

  useEffect(() => {
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [imageSrc, width, height, updateSize])

  const separatePointsByReference = useCallback(
    (points: {x: number; y: number; reference?: number}[]) => {
      return points.reduce(
        (acc, point) => {
          const ref = point.reference || 0
          if (!acc[ref]) acc[ref] = []
          acc[ref].push(point)
          return acc
        },
        {} as Record<number, typeof points>,
      )
    },
    [points],
  )

  const areaHasMultiplePoints = (points: Coordinate[]) => {
    return points.some((point) => point.reference && point.reference > 1)
  }

  const renderPolygon = (polygonPoints: Array<{x: number; y: number}>) => (
    <polygon
      points={polygonPoints.map((pt) => `${pt.x * renderedDim.width},${pt.y * renderedDim.height}`).join(' ')}
      fill='none'
      stroke='blue'
      strokeWidth='2'
    />
  )

  return (
    <Box display='inline-block' position='relative'>
      <img
        ref={imgRef}
        src={`data:image/jpeg;base64,${imageSrc}`}
        alt='snapshot'
        onLoad={updateSize}
        style={{display: 'block', width: '100%', maxWidth: width, height: 'auto'}}
      />

      {points && renderedDim.width > 0 && renderedDim.height > 0 && !areaHasMultiplePoints(points) && (
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: renderedDim.width,
            height: renderedDim.height,
            pointerEvents: 'none',
          }}
          xmlns='http://www.w3.org/2000/svg'>
          {renderPolygon(points)}
        </svg>
      )}

      {points &&
        renderedDim.width > 0 &&
        renderedDim.height > 0 &&
        areaHasMultiplePoints(points) &&
        Object.entries(separatePointsByReference(points)).map(([ref, groupedPoints]) => (
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: renderedDim.width,
              height: renderedDim.height,
              pointerEvents: 'none',
            }}
            xmlns='http://www.w3.org/2000/svg'
            key={ref}>
            {renderPolygon(groupedPoints)}
          </svg>
        ))}
    </Box>
  )
}
